import React, { useState, useEffect } from 'react';
import { Modal, ModalBody, ModalHeader, ModalFooter, LoadingIndicator, SimpleWizard, SimpleWizardStep } from '@ygtechnologies/bygr-frontend-component-library';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { Trans } from '@lingui/react/macro';
import { searchChangeRequests, mergeChangeRequests } from '@/services/change-requests';
//import ChangeRequestChooseParticipants from './ChangeRequestChooseParticipants';
import theme from './ChangeRequestMergeDialog.module.scss';
import { ValidationScope } from '@/components/forms/validation';
import { Button, ButtonBar, ButtonProps } from '@ygtechnologies/rubus-ui';
import { ChangeRequest, SellerChangeRequestStatus, ProjectTagType } from '@/types';
import { useValidationManager } from '@/utils/hooks';
import ChangeRequestMergeWizardStepEdit, { MergedChangeRequest } from './ChangeRequestMergeWizardStepEdit';
import ChangeRequestMergeWizardStepSelection from './ChangeRequestMergeWizardStepSelection';
import { uniqBy } from 'lodash-es';
import Tags from '@/components/Tags';
import Banner from '@/components/Banner';

enum Step {
    SelectChangeRequests,
    Edit,
    Confirm,
}

const Steps = [Step.SelectChangeRequests, Step.Edit, Step.Confirm];

interface ChangeRequestMergeDialogProps {
    residentialUnitId: number;
    onClose(): void;
    onSave(): void;
    show: boolean;
}

const ChangeRequestMergeDialog: React.FC<ChangeRequestMergeDialogProps> = ({ residentialUnitId, show, onClose, onSave }) => {
    const [changeRequests, setChangeRequests] = useState<ChangeRequest[] | null>(null);
    const [selectedChangeRequests, setSelectedChangeRequests] = useState<ChangeRequest[]>([]);
    const [step, setStep] = useState(Step.SelectChangeRequests);
    const [saving, setSaving] = useState(false);
    const [mergedChangeRequest, setMergedChangeRequest] = useState<MergedChangeRequest>({
        subject: '',
        description: '',
        tags: [],
    });

    useEffect(() => {
        setMergedChangeRequest({
            subject: selectedChangeRequests.map((x) => x.subject).join('/'),
            description: selectedChangeRequests.map((x) => x.description).join('\r\n\r\n'),
            tags: uniqBy(
                selectedChangeRequests.flatMap((x) => x.tags),
                'id'
            ),
        });
    }, [selectedChangeRequests]);

    const validationManager = useValidationManager([
        {
            isValid: (value, options) => selectedChangeRequests.length > 1,
            summaryMessage: ({ field }) => <Trans>You must select at least two change requests</Trans>,
        },
    ]);

    const handleOpen = async () => {
        setChangeRequests(null);
        setSelectedChangeRequests([]);
        setStep(Step.SelectChangeRequests);
        setSaving(false);

        setChangeRequests(
            await searchChangeRequests({
                units: [residentialUnitId],
                statuses: [SellerChangeRequestStatus.Unassigned],
            })
        );
    };

    const handleSubmit = async () => {
        setSaving(true);

        try {
            await mergeChangeRequests({
                changeRequestIds: selectedChangeRequests.map((cr) => cr.id),
                subject: mergedChangeRequest.subject,
                description: mergedChangeRequest.description,
                tags: mergedChangeRequest.tags.map((tag) => ({
                    ...tag,
                    type: ProjectTagType.Room,
                })),
            });

            triggerHandler(onSave);
            triggerHandler(onClose);
        } finally {
            setSaving(false);
        }
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    const saveButtonProps: Partial<ButtonProps> = {
        children: <Trans>Merge</Trans>,
        loading: saving,
    };

    const renderBody = () => {
        if (!changeRequests) {
            return <LoadingIndicator />;
        }

        if (changeRequests.length === 0) {
            return (
                <>
                    <ModalHeader>
                        <Trans>Merge change requests</Trans>
                    </ModalHeader>
                    <ModalBody>
                        <Banner type="warning">
                            <Trans>There are no change requests that can be merged.</Trans>
                        </Banner>
                    </ModalBody>
                    <ModalFooter>
                        <ButtonBar margins="none">
                            <ButtonBar.Right>
                                <Button variant="tertiary" onClick={handleCancelClick}>
                                    <Trans>Close</Trans>
                                </Button>
                            </ButtonBar.Right>
                        </ButtonBar>
                    </ModalFooter>
                </>
            );
        }

        return (
            <ModalBody>
                <ValidationScope validationManager={validationManager}>
                    <SimpleWizard
                        steps={Steps}
                        currentStep={step}
                        onStepChange={(step) => setStep(step)}
                        onSubmit={handleSubmit}
                        saveButtonProps={saveButtonProps}>
                        <SimpleWizardStep
                            stepNumber={Step.SelectChangeRequests}
                            header={<Trans>Select change requests to merge</Trans>}>
                            <ChangeRequestMergeWizardStepSelection
                                changeRequests={changeRequests}
                                onChange={setSelectedChangeRequests}
                                value={selectedChangeRequests}
                            />
                        </SimpleWizardStep>
                        <SimpleWizardStep stepNumber={Step.Edit} header={<Trans>Edit merged change request</Trans>}>
                            <ChangeRequestMergeWizardStepEdit
                                value={mergedChangeRequest!}
                                onChange={setMergedChangeRequest}
                            />
                        </SimpleWizardStep>

                        <SimpleWizardStep stepNumber={Step.Confirm} header={<Trans>Overview</Trans>}>
                            <Trans>The following change requests will be removed:</Trans>
                            <ul className={theme.overviewRemoved}>
                                {selectedChangeRequests.map((cr) => (
                                    <li key={cr.id}>
                                        #{cr.id} - {cr.subject}
                                    </li>
                                ))}
                            </ul>

                            <Trans>The following change request will be created:</Trans>
                            <div className={theme.overviewNew}>
                                <div className={theme.subject}>{mergedChangeRequest.subject}</div>
                                <div className={theme.description}>{mergedChangeRequest.description}</div>
                                <Tags tags={mergedChangeRequest.tags} size="small" />
                            </div>
                        </SimpleWizardStep>
                    </SimpleWizard>
                </ValidationScope>
            </ModalBody>
        );
    };

    return (
        <Modal isOpen={show} onOpen={handleOpen} toggle={handleCancelClick} size="lg">
            {renderBody()}
        </Modal>
    );
};

export default ChangeRequestMergeDialog;